import apiClient from './api';
import { getApiAccessToken } from './MapComponentsService';
import { GeometrySearchFilters } from '../types/GeometrySearch';
import { RIMSPolyline } from '../types/Polylines';
import { Point } from '../types/Point';
import { Polygon } from '../types/Polygon';
import { GeometryStatus } from '../types/BaseGeometry';
import {
  filterFetchedGeometries,
  type FilteredGeometrySearchResult,
} from '../utils/geometrySearchFilters';

const baseUrl = '/api/Geometry';

export type SearchGeometriesResponse = FilteredGeometrySearchResult;

/**
 * Search geometries based on multiple filter criteria
 * This service aggregates all geometry types and applies client-side filtering
 */
export const searchGeometries = async (
  addressSystemId: number,
  filters: GeometrySearchFilters
): Promise<SearchGeometriesResponse> => {
  const token = await getApiAccessToken();
  
  // Fetch all geometries based on contract status
  let allPolylines: RIMSPolyline[] = [];
  let allPoints: Point[] = [];
  let allPolygons: Polygon[] = [];
  let geometryStatuses: GeometryStatus[] = [];

  try {
    // If specific contracts are selected, fetch for each
    if (filters.selectedContractIds.length > 0) {
      const polylinePromises = filters.selectedContractIds.map(contractId =>
        apiClient.get(`${baseUrl}/getAllPolylines/${addressSystemId}/${contractId}`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      );
      const pointPromises = filters.selectedContractIds.map(contractId =>
        apiClient.get(`${baseUrl}/getAllPolypoints/${addressSystemId}/${contractId}`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      );
      const polygonPromises = filters.selectedContractIds.map(contractId =>
        apiClient.get(`${baseUrl}/getAllPolygons/${addressSystemId}/${contractId}`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      );

      if (filters.geometryType === 'all' || filters.geometryType === 'polyline') {
        const polylineResponses = await Promise.all(polylinePromises);
        allPolylines = polylineResponses.flatMap(res => res.data as RIMSPolyline[]);
      }
      if (filters.geometryType === 'all' || filters.geometryType === 'point') {
        const pointResponses = await Promise.all(pointPromises);
        allPoints = pointResponses.flatMap(res => res.data as Point[]);
      }
      if (filters.geometryType === 'all' || filters.geometryType === 'polygon') {
        const polygonResponses = await Promise.all(polygonPromises);
        allPolygons = polygonResponses.flatMap(res => res.data as Polygon[]);
      }
    } else {
      // Fetch all geometries for the address system
      if (filters.geometryType === 'all' || filters.geometryType === 'polyline') {
        const response = await apiClient.get(`${baseUrl}/getAllPolylinesByAddressSystem/${addressSystemId}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        allPolylines = response.data as RIMSPolyline[];
      }
      if (filters.geometryType === 'all' || filters.geometryType === 'point') {
        const response = await apiClient.get(`${baseUrl}/getAllPolypointsByAddressSystem/${addressSystemId}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        allPoints = response.data as Point[];
      }
      if (filters.geometryType === 'all' || filters.geometryType === 'polygon') {
        const response = await apiClient.get(`${baseUrl}/getAllPolygonsByAddressSystem/${addressSystemId}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        allPolygons = response.data as Polygon[];
      }
    }

    geometryStatuses = await apiClient.get(`/api/Geometry/status/${addressSystemId}`, {
        headers: { Authorization: `Bearer ${token}` }
    }).then(
        (res) => res.data as GeometryStatus[]
    );

    return filterFetchedGeometries(
      allPolylines,
      allPoints,
      allPolygons,
      filters,
      geometryStatuses,
    );
  } catch (error) {
    console.error('Error searching geometries:', error);
    throw error;
  }
};
